import { FormEvent, useState } from "react"

import { AiHelpSectionHit, searchAiHelp } from "@domain/ai_help"
import useTranslate from "@hooks/use_translate"
import SearchIcon from "@mui/icons-material/Search"
import {
  Alert,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material"

import { HelpImage } from "./ArticleDrawer"

/** The same search the Operator runs, so an admin can see what a question would be answered from. */
export default function SearchTab() {
  const { translate } = useTranslate()
  const [text, setText] = useState("")
  const [loading, setLoading] = useState(false)
  const [hits, setHits] = useState<AiHelpSectionHit[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!text.trim()) {
      return
    }
    setLoading(true)
    const response = await searchAiHelp({ query: text.trim() })
    setLoading(false)
    setError(
      response.isSuccess
        ? null
        : response.errorMessage || translate("ai_help_failed")
    )
    setHits(response.data ?? [])
  }

  return (
    <Stack gap={2}>
      <Stack component="form" direction="row" gap={1} onSubmit={submit}>
        <TextField
          size="small"
          fullWidth
          placeholder={translate("ai_help_search_placeholder")}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <Button
          type="submit"
          variant="contained"
          startIcon={<SearchIcon />}
          disabled={loading}
        >
          {translate("ai_help_search")}
        </Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {hits?.length === 0 && !error && (
        <Alert severity="warning">{translate("ai_help_search_empty")}</Alert>
      )}
      {hits?.map((hit) => (
        <Paper
          key={`${hit.articleId}:${hit.contentId}`}
          variant="outlined"
          sx={{ p: 2 }}
        >
          <Stack gap={1}>
            <Stack direction="row" gap={1} alignItems="center">
              <Chip size="small" label={hit.score.toFixed(2)} />
              <Typography variant="subtitle2">
                {hit.title} › {hit.heading}
              </Typography>
            </Stack>
            <Typography variant="body2" whiteSpace="pre-wrap">
              {hit.text}
            </Typography>
            {hit.images.map((image) => (
              <HelpImage key={image.key} image={image} />
            ))}
          </Stack>
        </Paper>
      ))}
    </Stack>
  )
}
